"use client";

import { FC, RefObject, useEffect, useRef, createRef } from "react";

import { useStyles } from "./styles";
import {
  HistogramBuckets,
  QuizLobbyPlayer,
  QuizType,
  QaQuizLeaderboardItem,
  CategoryQuizLeaderboardItem,
} from "@/types/quiz";
import { useAppSelector } from "@/redux/store";
import { isQaQuizLeaderboardItem } from "../helpers";
import QuestionAnswerLeaderboardRow from "../QuestionAnswerLeaderboardRow";
import CategoryLeaderboardRow from "../CategoryLeaderboardRow";
import Histogram from "../Histogram";

const createLeaderboardRefs = (
  rows: QaQuizLeaderboardItem[] | CategoryQuizLeaderboardItem[]
) => {
  const refs: { [key: string]: RefObject<HTMLDivElement> } = {};
  rows.forEach((row) => {
    let id = `user-${row.rank}`;
    if (isQaQuizLeaderboardItem(row)) {
      if (row.userId) id = row.userId.toString();
    }
    refs[id] = createRef();
  });
  return refs;
};

interface LeaderboardProps {
  leaderboardType: "quiz" | "overall";
  leaderboardItems: QaQuizLeaderboardItem[] | CategoryQuizLeaderboardItem[];
  histogramBuckets?: HistogramBuckets;
  quizType: QuizType;
  userResult?: string | null;
  userScore?: number | null;
  userPercentile?: number | null;
  player?: QuizLobbyPlayer | null;
}

const Leaderboard: FC<LeaderboardProps> = ({
  leaderboardType,
  leaderboardItems,
  histogramBuckets = null,
  quizType,
  userResult,
  userScore = null,
  userPercentile = null,
  player = null,
}) => {
  const user = useAppSelector((state) => state.user.user);
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const leaderboardRefs = createLeaderboardRefs(leaderboardItems);

  useEffect(() => {
    if (user) {
      if (quizType === QuizType.QuestionAnswer) {
        const qaLeaderboardItems = [
          ...leaderboardItems,
        ] as QaQuizLeaderboardItem[];
        const userLeaderboardItem = qaLeaderboardItems.find(
          (item) => item.userId === user.id
        );
        if (!userLeaderboardItem) return;
      }

      const element = leaderboardRefs[user.id.toString()];
      if (element?.current) {
        element.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [user, leaderboardItems]);

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.content}>
        {leaderboardItems.length > 0 ? (
          leaderboardItems.map((leaderboardItem, index) => {
            const refId =
              isQaQuizLeaderboardItem(leaderboardItem) && leaderboardItem.userId
                ? leaderboardItem.userId.toString()
                : `user-${index + 1}`;
            return quizType === QuizType.QuestionAnswer ? (
              <QuestionAnswerLeaderboardRow
                key={`user-${index + 1}`}
                leaderboardType={leaderboardType}
                leaderboardItem={leaderboardItem as QaQuizLeaderboardItem}
                ref={leaderboardRefs[refId]}
                player={player}
              />
            ) : (
              <CategoryLeaderboardRow
                key={`user-${index + 1}`}
                leaderboardItem={leaderboardItem as CategoryQuizLeaderboardItem}
                userResult={userResult}
              />
            );
          })
        ) : (
          <div className={classes.placeholder}>
            <p className={classes.placeholderText}>No results yet!</p>
          </div>
        )}
        {leaderboardType === "quiz" && histogramBuckets && (
          <div className={classes.histogramContainer}>
            <Histogram
              histogramBuckets={histogramBuckets}
              userScore={userScore}
              userPercentile={userPercentile}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
